import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { format, isValid, parseISO } from 'date-fns'
import { useTheme } from 'styled-components/native'

import { Prescription } from '@lyrahealth-inc/shared-app-logic/src/models/prescription/Prescription'

import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { RowView } from '../../templates/content/CommonViews'

const DispenseUnitTypeIdMap = {
  35: 'Applicator',
  39: 'Blister',
  44: 'Caplet',
  4: 'Capsule',
  32: 'Each',
  52: 'Film',
  8: 'Gram',
  33: 'Gum',
  54: 'Implant',
  57: 'Insert',
  11: 'Kit',
  90: 'Lancet',
  13: 'Lozenge',
  15: 'ml',
  21: 'Packet',
  64: 'Pad',
  28: 'Patch',
  83: 'Pen Needle',
  70: 'Ring',
  73: 'Sponge',
  75: 'Stick',
  76: 'Strip',
  23: 'Suppository',
  77: 'Swab',
  26: 'Tablet',
  81: 'Troche',
  19: 'Unspecified',
  82: 'Wafer',
}

const specialPlurals: Record<string, string> = {
  Suppository: 'Suppositories',
  Lozenge: 'Lozenges',
  Patch: 'Patches',
  Each: 'Each',
  Gum: 'Gum',
  Film: 'Films',
}

const pluralizeDispenseUnit = (quantity: number, unitType: string): string => {
  if (quantity === 1) {
    return unitType
  }

  if (specialPlurals[unitType]) {
    return specialPlurals[unitType]
  }

  return `${unitType}s`
}

const useMedicationsListColumns = () => {
  const { colors, spacing } = useTheme()
  const { formatMessage } = useIntl()

  return useMemo(() => {
    const columnHelper = createColumnHelper<Prescription>()

    const columns: ColumnDef<Prescription, any>[] = [
      columnHelper.accessor('DisplayName', {
        header: 'Medication',
        enableSorting: true,
        size: 368,
        meta: {
          inlineComponent: true,
        },
        cell: ({ getValue }) => {
          const medicationName = getValue()
          return (
            <div
              style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: colors.textPrimary,
                padding: `${spacing['24px']} ${spacing['16px']}`,
              }}
            >
              {medicationName}
            </div>
          )
        },
      }),
      columnHelper.accessor('Quantity', {
        header: 'Quantity',
        meta: {
          inlineComponent: true,
        },
        cell: ({ row }) => {
          const rawQuantity = row.original.Quantity
          const quantity = Number(rawQuantity) || 0
          const dispenseUnitId = row.original.DispenseUnitId
          const unitType = DispenseUnitTypeIdMap[dispenseUnitId as keyof typeof DispenseUnitTypeIdMap]
          const directions = row.original.Directions
          const refills = row.original.Refills

          let quantityText = `${quantity}`
          if (unitType) {
            const pluralizedUnit = pluralizeDispenseUnit(quantity, unitType)
            quantityText = `${quantity} ${pluralizedUnit}`
          }

          // Create refills text if refills exist
          let refillsText = ''
          if (refills !== undefined && refills !== null && refills !== '') {
            const refillsNum = Number(refills) || 0
            if (refillsNum > 0) {
              const refillsLabel = refillsNum === 1 ? 'refill' : 'refills'
              refillsText = `${refillsNum} ${refillsLabel}`
            }
          }

          // Create the content with quantity on top and refills below
          const content = (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <div
                style={{
                  fontSize: '16px',
                  color: colors.textPrimary,
                  marginBottom: spacing['4px'],
                }}
              >
                {quantityText}
              </div>
              {refillsText && (
                <div
                  style={{
                    fontSize: '14px',
                    color: colors.textSecondary,
                  }}
                >
                  {refillsText}
                </div>
              )}
            </div>
          )

          // Only show tooltip if directions exist and are not empty
          if (directions && directions.trim()) {
            return (
              <RowView style={{ alignItems: 'center', gap: 8 }}>
                {content}
                <Tooltip content={directions} hoverEnabled placement='top'>
                  <InfoIcon size={16} />
                </Tooltip>
              </RowView>
            )
          }

          return content
        },
      }),
      columnHelper.accessor('MedicationStatus', {
        header: 'Medication status',
        enableSorting: true,
        meta: {
          inlineComponent: true,
        },
        cell: ({ row }) => {
          const medicationStatus = row.original.MedicationStatus
          const dateInactive = row.original.DateInactive

          // Format the inactive date if it exists
          let formattedInactiveDate = ''
          if (dateInactive) {
            try {
              const parsedDate = parseISO(dateInactive)
              if (isValid(parsedDate)) {
                formattedInactiveDate = format(parsedDate, 'M/d/yyyy')
              } else {
                formattedInactiveDate = dateInactive
              }
            } catch {
              formattedInactiveDate = dateInactive
            }
          }

          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '2px',
                padding: `${spacing['24px']} ${spacing['16px']}`,
              }}
            >
              <div
                style={{
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: formattedInactiveDate ? colors.textWarning : colors.textPrimary,
                }}
              >
                {medicationStatus}
              </div>
              {formattedInactiveDate && (
                <div
                  style={{
                    fontSize: '14px',
                    lineHeight: '1.5',
                    color: colors.textSecondary,
                  }}
                >
                  {formatMessage(
                    {
                      defaultMessage: 'on {date}',
                      description: 'Label showing when a medication became inactive',
                    },
                    { date: formattedInactiveDate },
                  )}
                </div>
              )}
            </div>
          )
        },
      }),
      columnHelper.accessor('WrittenDate', {
        header: 'Prescribed on',
        enableSorting: true,
        cell: ({ getValue }) => {
          const dateValue = getValue()
          if (!dateValue) return ''

          try {
            const parsedDate = parseISO(dateValue)
            if (isValid(parsedDate)) {
              return format(parsedDate, 'M/d/yyyy')
            }
            return dateValue
          } catch {
            return dateValue
          }
        },
        sortingFn: (rowA, rowB) => {
          const aDate = rowA.original.WrittenDate
          const bDate = rowB.original.WrittenDate

          // Handle empty dates - put them at the end
          if (!aDate && !bDate) return 0
          if (!aDate) return 1
          if (!bDate) return -1

          try {
            const aParsed = parseISO(aDate)
            const bParsed = parseISO(bDate)

            if (isValid(aParsed) && isValid(bParsed)) {
              return aParsed.getTime() - bParsed.getTime()
            }

            // Fallback to string comparison if dates are invalid
            return aDate.localeCompare(bDate)
          } catch {
            return aDate.localeCompare(bDate)
          }
        },
      }),
      columnHelper.accessor('Status', {
        header: 'Prescription status',
        enableSorting: true,
        meta: {
          inlineComponent: true,
        },
        cell: ({ getValue }) => {
          const status = getValue()
          const isWarningText = status === 'Deleted' || status === 'Error'

          return (
            <div
              style={{
                fontSize: '14px',
                lineHeight: '1.5',
                color: isWarningText ? colors.textWarning : colors.textPrimary,
                padding: `${spacing['24px']} ${spacing['16px']}`,
              }}
            >
              {status}
            </div>
          )
        },
      }),
    ]

    return columns
  }, [colors.textPrimary, colors.textSecondary, colors.textWarning, spacing, formatMessage])
}

export default useMedicationsListColumns
